import { maybeQueueMicrotask } from './maybeQueueMicrotask';
import { withResolvers } from './withResolvers';

// Core function types
export type IdentityFn<TInput> = (input: TInput) => string;

export type PartitionFn<TInput, TPartition> = (input: TInput) => TPartition;

export type ExecuteFn<TInput, TBatchResponse, TPartition> = (
  batch: Record<string, TInput>,
  partition: TPartition
) => Promise<TBatchResponse> | TBatchResponse;

export type ExtractFn<TInput, TResult, TBatchResponse> = (args: {
  response: TBatchResponse;
  input: TInput;
  identity: string;
}) => TResult;

// Internal data structures
type PendingCallData<TInput, TResult, TPartition> = {
  identityKey: string;
  promise: Promise<TResult>;
  input: TInput;
  partition: TPartition;
  resolve: (value: TResult) => void;
  reject: (error: any) => void;
};

type BatchData = {
  calls: Set<string>;
  timeoutId?: ReturnType<typeof setTimeout>;
  maxTimeoutId?: ReturnType<typeof setTimeout>;
  microtaskScheduled?: boolean;
};

// Batch configuration types
type BatchConfig<
  TMode extends 'read' | 'write',
  TInput,
  TResult,
  TBatchResponse,
  TPartition,
> = {
  identity: IdentityFn<TInput>;
  partition: PartitionFn<TInput, TPartition>;
  execute: ExecuteFn<TInput, TBatchResponse, TPartition>;
  extract: ExtractFn<TInput, TResult, TBatchResponse>;
  chunkSize: number;
  mode: TMode;
} & (
  | { type: 'debounce'; timeMs: number; maxWaitMs?: number }
  | { type: 'window'; timeMs: number }
  | { type: 'immediate' }
);

/**
 * Smart promise batching with configurable timing strategies and deduplication semantics.
 *
 * Collects and dispatches batches of calls according to the configured timing strategy.
 * Promises are intelligently reused when duplicates are detected based on identity.
 *
 * Error handling:
 * - Batch execution errors reject all calls in that batch
 * - Individual extraction errors only reject that specific call
 * - Concurrent batches are isolated - errors don't cascade
 *
 * Deduplication behavior depends on mode:
 * - Read mode: First-write-wins, dedupes across both pending and dispatched (in-flight) calls
 * - Write mode: Last-write-wins, only dedupes pending calls (creates new batch after dispatch)
 */
export type BatchScheduler<TInput, TResult, TPartition> = {
  /**
   * Add a call to the batch and receive a promise that resolves to the result.
   *
   * Duplicate calls (same identity) are automatically deduplicated according to the mode:
   * - Read mode: Returns the existing promise for pending or dispatched calls
   * - Write mode: Updates pending call with latest data, returns shared promise
   */
  queue(input: TInput): Promise<TResult>;

  /**
   * Cancel all pending calls, rejecting their promises with 'Cancelled' error.
   *
   * Primarily intended for cleaning up between test specs. In-flight batches may
   * still complete and could affect deduping of identical calls dispatched after abort.
   */
  abort(): void;

  /**
   * Immediately dispatch all pending batches and wait for all in-flight calls to settle.
   *
   * This is a cleaner alternative to abort() that ensures all queued work completes.
   * Rejections are swallowed by flush() itself (always resolves), but are still
   * surfaced to the original queuer's promise.
   *
   * Synchronous dispatch guarantee: flush() synchronously invokes the execute
   * callback for each pending batch before returning. The execute function is
   * always called synchronously regardless of whether it is sync or async —
   * an async executor will run synchronously up until its first yield point.
   * This is safe to rely on for patterns like tracking batch size externally
   * and flushing before a budget is exceeded.
   */
  flush(): Promise<void>;

  /**
   * Run the configured identity function on arbitrary input.
   *
   * Useful for composition patterns like cache wrappers that need to compute
   * the same identity key the scheduler uses for deduplication.
   */
  identity: IdentityFn<TInput>;

  /**
   * Run the configured partition function on arbitrary input.
   *
   * Useful for composition patterns that need to determine which partition
   * a call would belong to without actually queuing it.
   */
  partition: PartitionFn<TInput, TPartition>;
};

/**
 * Read-mode batch scheduler with aggressive deduplication.
 *
 * Deduplication strategy:
 * - First-write-wins: Duplicate calls return the same promise
 * - Dedupes against both pending AND dispatched (in-flight) requests
 * - Perfect for data fetching where multiple calls for the same resource should collapse
 *
 * Edge cases:
 * - Subsequent calls with same identity but different partition will dedupe into the original partition
 */
export type ReadBatchScheduler<TInput, TResult, TPartition> = BatchScheduler<
  TInput,
  TResult,
  TPartition
> & {
  readonly __mode: 'read';
};

/**
 * Write-mode batch scheduler with last-write-wins semantics.
 *
 * Deduplication strategy:
 * - Last-write-wins: Latest data takes precedence for pending calls
 * - Only dedupes pending calls (not dispatched - creates new batch instead)
 * - Perfect for updates where the most recent value matters
 *
 * Edge cases:
 * - Subsequent calls with same identity but different partition will dedupe into the new partition
 */
export type WriteBatchScheduler<TInput, TResult, TPartition> = BatchScheduler<
  TInput,
  TResult,
  TPartition
> & {
  readonly __mode: 'write';
};

export const createBatchScheduler = <
  TMode extends 'read' | 'write',
  TInput,
  TResult,
  TBatchResponse,
  TPartition,
>(
  configInput: BatchConfig<TMode, TInput, TResult, TBatchResponse, TPartition>
) => {
  // Clone & freeze config to prevent bugs from mutation
  const config = Object.freeze({ ...configInput });

  // Track all current batches, tracking calls that will be fulfilled with a single request with their timing controls
  const batches = new Map<TPartition, BatchData>();

  // Track all pending calls (those whose underlying request hasn't yet been dispatched)
  const pendingCalls = new Map<
    string,
    PendingCallData<TInput, TResult, TPartition>
  >();

  // Track all dispatched calls (those whose request has been dispatched and are awaiting a response)
  const dispatchedCalls = new Map<
    string,
    PendingCallData<TInput, TResult, TPartition>
  >();

  const takeBatch = (partition: TPartition) => {
    const batchData = batches.get(partition);

    batches.delete(partition);

    clearTimeout(batchData?.timeoutId);
    clearTimeout(batchData?.maxTimeoutId);

    return batchData;
  };

  // Execute a batch of calls for a given partition
  const executeBatch = async (partition: TPartition) => {
    const batchData = takeBatch(partition);

    // If the batch is empty, we can just return
    if (!batchData || batchData.calls.size === 0) {
      return;
    }

    // Get call data from pendingCalls, copy into dispatched
    const callsData: PendingCallData<TInput, TResult, TPartition>[] = [];

    for (const identityKey of batchData.calls) {
      const callData = pendingCalls.get(identityKey);

      if (callData) {
        callsData.push(callData);
        dispatchedCalls.set(identityKey, callData);
        pendingCalls.delete(identityKey);
      }
    }

    try {
      // Build execute parameter
      const batch = {} as Record<string, TInput>;
      for (const { identityKey, input } of callsData) {
        batch[identityKey] = input;
      }

      // Execute this batch
      const response = await config.execute(batch, partition);

      for (const { identityKey, input, resolve, reject } of callsData) {
        try {
          resolve(config.extract({ response, input, identity: identityKey }));
        } catch (error) {
          // Reject individual calls if extraction rejects
          reject(error);
        }

        dispatchedCalls.delete(identityKey);
      }
    } catch (error) {
      // Reject entire batch if request fails
      for (const { identityKey, reject } of callsData) {
        reject(error);

        dispatchedCalls.delete(identityKey);
      }
    }
  };

  const queueUnsafe = (input: TInput) => {
    const identityKey = config.identity(input);
    const partition = config.partition(input);

    // Check for duplicates
    if (config.mode === 'read') {
      const existingCallData =
        pendingCalls.get(identityKey) || dispatchedCalls.get(identityKey);

      if (existingCallData) {
        return existingCallData.promise;
      }
    } else if (config.mode === 'write') {
      // Only dedupe pending calls in write mode
      const existingCallData = pendingCalls.get(identityKey);

      if (existingCallData) {
        const oldPartition = existingCallData.partition;

        // Update the existing call's input and partition (last-write-wins)
        existingCallData.input = input;
        existingCallData.partition = partition;

        // If same partition, we're done and can return the existing promise
        if (oldPartition === partition) {
          return existingCallData.promise;
        }

        // Otherwise, we're switching partitions
        const oldBatchData = batches.get(oldPartition);
        if (oldBatchData) {
          // Remove the call from the old partition
          oldBatchData.calls.delete(identityKey);

          // If the old partition is now empty, clean it up
          if (oldBatchData.calls.size === 0) {
            takeBatch(oldPartition);
          }
        }

        // Fall through to add to new partition
      }
    }

    // If we get here with a value in pendingCalls, we need to reuse it (only relevant when switching batches in write mode).
    // Otherwise, we need to create a new promise.
    const { promise, resolve, reject } =
      pendingCalls.get(identityKey) ?? withResolvers<TResult>();

    const callData: PendingCallData<TInput, TResult, TPartition> = {
      identityKey,
      promise,
      input,
      partition,
      resolve,
      reject,
    };

    pendingCalls.set(identityKey, callData);

    // Add identity to batch
    const batchData: BatchData = batches.get(partition) ?? {
      calls: new Set(),
    };

    batchData.calls.add(identityKey);

    batches.set(partition, batchData);

    // Execute the batch immediately if we've reached or somehow surpassed the chunk size
    if (batchData.calls.size >= config.chunkSize) {
      void executeBatch(partition);

      return promise;
    }

    if (config.type === 'debounce') {
      // If debouncing, clear and re-set the timeout
      if (batchData.timeoutId) {
        clearTimeout(batchData.timeoutId);
      }

      batchData.timeoutId = setTimeout(
        () => executeBatch(partition),
        config.timeMs
      );

      // Set a max timeout
      if (config.maxWaitMs && !batchData.maxTimeoutId) {
        batchData.maxTimeoutId = setTimeout(
          () => executeBatch(partition),
          config.maxWaitMs
        );
      }
    } else if (config.type === 'window') {
      // If windowing, set the timeout if it doesn't already exist
      if (!batchData.timeoutId) {
        batchData.timeoutId = setTimeout(
          () => executeBatch(partition),
          config.timeMs
        );
      }
    } else if (config.type === 'immediate') {
      // If immediate, queue a microtask to execute the batch
      if (!batchData.microtaskScheduled) {
        batchData.microtaskScheduled = true;

        maybeQueueMicrotask(() => void executeBatch(partition));
      }
    }

    return promise;
  };

  /**
   * Queue a call in the batch scheduler.
   *
   * Returns a promise that will resolve to the result of the call.
   */
  const queue = (input: TInput) => {
    try {
      return queueUnsafe(input);
    } catch (e) {
      return Promise.reject(e);
    }
  };

  /**
   * Abort all pending calls. Primarily intended for cleaning up between test specs.
   *
   * Note: In-flight batches may still complete and could affect deduping
   * of identical calls dispatched after abort.
   */
  const abort = () => {
    // Clear all timeouts
    for (const partition of batches.keys()) {
      takeBatch(partition);
    }

    // Reject all external promises
    for (const callData of [
      ...pendingCalls.values(),
      ...dispatchedCalls.values(),
    ]) {
      callData.reject(Error('Cancelled'));
    }

    // Clear all maps
    batches.clear();
    pendingCalls.clear();
    dispatchedCalls.clear();
  };

  /**
   * Flush all pending batches. This is a cleaner version of abort that immediately dispatches any pending calls
   * then waits on all dispatched calls to settle.
   *
   * Note: rejections will be swallowed here (i.e flush always resolves), but are still surfaced to the queuer.
   */
  const flush = async () => {
    // Execute all pending batches immediately.
    // executeBatch is async but runs synchronously up to `await config.execute(...)`.
    // For sync execute functions, this means the callback fires synchronously here.
    for (const partitionKey of batches.keys()) {
      void executeBatch(partitionKey);
    }

    const allPromises = [...dispatchedCalls.values()].map(
      (callData) => callData.promise
    );

    // Wait for all dispatched calls to complete
    await Promise.allSettled(allPromises);
  };

  // Ensure we don't lose type checking
  const scheduler: BatchScheduler<TInput, TResult, TPartition> = {
    queue,
    abort,
    flush,
    identity: config.identity,
    partition: config.partition,
  };

  // Assert as the correct "mode" (uses branded types to differentiate)
  return scheduler as TMode extends 'read'
    ? ReadBatchScheduler<TInput, TResult, TPartition>
    : WriteBatchScheduler<TInput, TResult, TPartition>;
};
